import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { DBCrmMeeting, DBMeetingSummary, DBMeetingTranscript } from "@/lib/db-types";

/** Møtet referatsiden gjelder (inkl. vert og kunde for header). */
export interface MeetingForSummary extends DBCrmMeeting {
  host: { id: string; full_name: string } | null;
  booker: { id: string; full_name: string } | null;
  customer: { id: string; name: string } | null;
  lead: { id: string; name: string } | null;
}

const MEETING_SELECT =
  "*, host:users!crm_meetings_host_user_id_fkey(id, full_name), booker:users!crm_meetings_booked_by_fkey(id, full_name), customer:customers!crm_meetings_customer_id_fkey(id, name), lead:leads!crm_meetings_lead_id_fkey(id, name)";

export function useMeeting(meetingId: string | undefined) {
  return useQuery({
    queryKey: ["crm-meetings", meetingId],
    queryFn: async (): Promise<MeetingForSummary | null> => {
      const { data, error } = await supabase
        .from("crm_meetings")
        .select(MEETING_SELECT)
        .eq("id", meetingId!)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as MeetingForSummary | null) ?? null;
    },
    enabled: !!meetingId,
    staleTime: 60 * 1000,
  });
}

/** Aktivt referat (utkast eller godkjent) for møtet; null når det ikke finnes. */
export function useMeetingSummary(meetingId: string | undefined) {
  return useQuery({
    queryKey: ["meeting-summary", meetingId],
    queryFn: async (): Promise<DBMeetingSummary | null> => {
      const { data, error } = await supabase
        .from("meeting_summaries")
        .select("*")
        .eq("meeting_id", meetingId!)
        .in("status", ["draft", "approved"])
        .maybeSingle();
      if (error) throw error;
      return (data as DBMeetingSummary | null) ?? null;
    },
    enabled: !!meetingId,
    staleTime: 30 * 1000,
  });
}

/**
 * Rå transkripsjon. RLS gir bare vert, booker, kundeeiere og admin tilgang, og
 * raden er slettet etter crm_transcript_retention_days; null betyr derfor
 * «ingen tilgang eller slettet».
 */
export function useMeetingTranscript(meetingId: string | undefined) {
  return useQuery({
    queryKey: ["meeting-transcript", meetingId],
    queryFn: async (): Promise<DBMeetingTranscript | null> => {
      const { data, error } = await supabase
        .from("meeting_transcripts")
        .select("*")
        .eq("meeting_id", meetingId!)
        .maybeSingle();
      if (error) throw error;
      return (data as DBMeetingTranscript | null) ?? null;
    },
    enabled: !!meetingId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Møtet bak et godkjenningskort (summary_approval-oppfølgingen peker på møtets aktivitet i
 * kontaktloggen). Brukes til «Åpne referatet». null når aktiviteten ikke hører til et møte.
 */
export function useMeetingIdForActivity(activityId: string | null | undefined) {
  return useQuery({
    queryKey: ["crm-meetings", "by-activity", activityId],
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.from("crm_meetings").select("id").eq("activity_id", activityId!).maybeSingle();
      if (error) throw error;
      return (data as { id: string } | null)?.id ?? null;
    },
    enabled: !!activityId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Møtene på en prosess (lead-prosesser har ingen kundeside å vise dem på). Bundet av opportunity_id. */
export function useOpportunityMeetings(opportunityId: string | undefined) {
  return useQuery({
    queryKey: ["crm-meetings", "by-opportunity", opportunityId],
    queryFn: async (): Promise<Pick<DBCrmMeeting, "id" | "title" | "start_at" | "status" | "customer_id">[]> => {
      const { data, error } = await supabase
        .from("crm_meetings")
        .select("id, title, start_at, status, customer_id")
        .eq("opportunity_id", opportunityId!)
        .order("start_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!opportunityId,
    staleTime: 60 * 1000,
  });
}
